import { useEffect, useState } from 'react';

interface ToastProps {
  id: string;
  message: string;
  type: 'success' | 'error' | 'warning' | 'info';
  duration?: number;
  onClose: (id: string) => void;
  position: number;
  undoAction?: () => void;
  undoLabel?: string;
}

export default function Toast({
  id,
  message,
  type,
  duration = 5000,
  onClose,
  position,
  undoAction,
  undoLabel = 'Отменить',
}: ToastProps) {
  const [isVisible, setIsVisible] = useState(false);
  const [progress, setProgress] = useState(100);
  const [isHovered, setIsHovered] = useState(false);
  const [isLeaving, setIsLeaving] = useState(false);

  useEffect(() => {
    // Spring entrance animation with slight delay for stacked toasts
    const timer = setTimeout(() => setIsVisible(true), position * 50);
    return () => clearTimeout(timer);
  }, [position]);

  useEffect(() => {
    if (isHovered) return; // Pause progress when hovered

    const interval = setInterval(() => {
      setProgress((prev) => {
        const newProgress = prev - 100 / (duration / 100);
        if (newProgress <= 0) {
          handleClose();
          return 0;
        }
        return newProgress;
      });
    }, 100);

    return () => clearInterval(interval);
  }, [duration, isHovered]);

  const handleClose = () => {
    setIsLeaving(true);
    setIsVisible(false);
    setTimeout(() => onClose(id), 400);
  };

  const handleUndoClick = () => {
    undoAction?.();
    handleClose();
  };

  const typeConfig = {
    success: {
      bg: 'bg-white',
      border: 'border-green-200',
      text: 'text-green-800',
      icon: 'ri-check-circle-line text-green-500',
      progress: 'bg-green-500',
      shadow: 'shadow-green-100',
    },
    error: {
      bg: 'bg-white',
      border: 'border-red-200',
      text: 'text-red-800',
      icon: 'ri-error-warning-line text-red-500',
      progress: 'bg-red-500',
      shadow: 'shadow-red-100',
    },
    warning: {
      bg: 'bg-white',
      border: 'border-orange-200',
      text: 'text-orange-800',
      icon: 'ri-alert-line text-orange-500',
      progress: 'bg-orange-500',
      shadow: 'shadow-orange-100',
    },
    info: {
      bg: 'bg-white',
      border: 'border-blue-200',
      text: 'text-blue-800',
      icon: 'ri-information-line text-blue-500',
      progress: 'bg-blue-500',
      shadow: 'shadow-blue-100',
    },
  };

  const config = typeConfig[type];

  return (
    <div
      className={`
        fixed right-4 w-80 max-w-sm rounded-lg border z-50
        transform transition-all duration-400 ease-out will-change-transform
        ${
          isVisible && !isLeaving
            ? 'translate-x-0 opacity-100 scale-100'
            : 'translate-x-full opacity-0 scale-95'
        }
        ${config.bg} ${config.border} ${config.shadow}
        shadow-lg hover:shadow-xl
        animate-spring-bounce
      `}
      style={{
        top: `${80 + position * 90}px`,
        animationDelay: `${position * 50}ms`,
        zIndex: 1000 + (10 - position), // Higher z-index for newer toasts
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className="p-4">
        <div className="flex items-start">
          <div className="flex-shrink-0">
            <i className={`${config.icon} text-xl animate-fade-in`}></i>
          </div>
          <div className="ml-3 flex-1 min-w-0">
            <p className={`text-sm font-medium ${config.text} leading-relaxed`}>
              {message}
            </p>
            {undoAction && (
              <button
                onClick={handleUndoClick}
                className={`
                  mt-2 text-xs font-medium underline transition-colors duration-200
                  ${config.text} hover:opacity-80 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-opacity-50
                `}
              >
                {undoLabel}
              </button>
            )}
          </div>
          <div className="ml-4 flex-shrink-0">
            <button
              onClick={handleClose}
              className={`
                inline-flex rounded-md p-1.5 transition-all duration-200
                ${config.text} hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-transparent
                hover:scale-110 active:scale-95
              `}
            >
              <i className="ri-close-line text-sm"></i>
            </button>
          </div>
        </div>
      </div>

      {/* Animated progress bar */}
      <div className="h-1 bg-gray-100 rounded-b-lg overflow-hidden">
        <div
          className={`
            h-full transition-all duration-100 ease-linear will-change-transform
            ${config.progress}
            ${isHovered ? 'animate-pulse' : ''}
          `}
          style={{
            width: `${progress}%`,
            transformOrigin: 'left center',
          }}
        />
      </div>
    </div>
  );
}
